import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  InputAdornment,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { OnboardAction } from "schemas/dashboard";
import { CustomEvent, Map } from "schemas/functions";
import { INSTAGRAM_USERNAME_URL } from "schemas/social";

import { AFFILIATE_TERMS_AND_CONDITIONS_LINK } from "constants/influencer";
import {
  BOTH,
  OPT_IN_EMAIL,
  OPT_IN_PHONE,
  getRadioOption,
  setNotificationsFromRadioOption,
} from "features/Settings/UserNotifications/helpers";
import { fetcherAuth } from "utils/api";
import { normalizePhone } from "utils/form";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";
import { useTracking } from "utils/useTracking";

import { isValidUsername, sanitizeInput } from "../AboutForm/helpers";
import styles from "../styles";

interface Props {
  handleBack: () => void;
  handleSkip?: () => void;
  handleNext: () => void;
}

const FinalOptInForm = ({ handleSkip, handleNext, handleBack }: Props) => {
  const {
    currentOrg,
    setUserNotifications,
    userNotifications,
    profile,
    setProfile,
  } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const [option, setOption] = useState<string>(
    getRadioOption(userNotifications),
  );
  const { setErrorAlert } = useContext(AlertContext);
  const [instagramUsername, setInstagramUsername] = useState<string>("");
  const [joinedAffiliate, setJoinedAffiliate] = useState<string>("");
  const [instagramUsernameValidation, setInstagramUsernameValidation] =
    useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const handleChange = (e: CustomEvent) => {
    let { value } = e.target;
    if (value === "") value = OPT_IN_EMAIL;
    setOption(value);
    setNotificationsFromRadioOption(value, setUserNotifications);
  };
  const { trackUserHasOnboarded } = useTracking();

  const submitForm = async () => {
    const data: Map = {
      optInPhone: userNotifications?.optInPhone,
      optInEmail: userNotifications?.optInEmail,
    };
    if (userNotifications?.optInPhone && !profile?.phoneNumber) {
      setError(
        "Please provide a phone number to receive text message notifications.",
      );
      return;
    }
    if (instagramUsername) {
      data.instagramUrl = `${INSTAGRAM_USERNAME_URL}${instagramUsername}`;
      setProfile((prev) => {
        if (!prev) return prev;
        const copy = makeDeepCopy(prev);
        copy.instagramUrl = data.instagramUrl;
        return copy;
      });
    }
    if (profile?.phoneNumber) {
      data.phoneNumber = profile?.phoneNumber;
    }
    if (joinedAffiliate === "true") {
      _handleAutoCreateAffiliateOrganization();
    }
    setLoading(true);
    trackUserHasOnboarded(OnboardAction.FINAL_OPT_IN);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/settings`,
        "PUT",
        {},
        data,
      );
      handleNext();
      setError("");
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const _handleAutoCreateAffiliateOrganization = async () => {
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/affiliate-organizations`,
        "POST",
      );
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleChangePhone = (e: CustomEvent) => {
    const { value } = e.target;
    const copy = makeDeepCopy(profile);
    copy.phoneNumber = value;
    setProfile(copy);
    setError("");
  };

  const handleChangeInstagramUsername = (e: CustomEvent) => {
    setInstagramUsername(e.target.value);
  };

  const handleBlurred = (e: CustomEvent) => {
    const { value } = e.target;
    const sanitizedInput = sanitizeInput(value.trim());

    if (sanitizedInput && !isValidUsername(sanitizedInput)) {
      setInstagramUsernameValidation(
        "Must contain only letters, numbers, underscores, hyphens or periods",
      );
    } else {
      setInstagramUsername(sanitizeInput);
      setInstagramUsernameValidation("");
    }
  };

  useEffect(() => {
    if (profile?.instagramUrl && !instagramUsername) {
      setInstagramUsername(sanitizeInput(profile?.instagramUrl));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.instagramUrl]);

  useEffect(() => {
    if (option === "") {
      setOption(BOTH);
      setUserNotifications({
        optInEmail: true,
        optInPhone: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <Typography variant="h3" sx={{ mb: 3, textAlign: "center" }}>
        ✨ Final Step ✨
      </Typography>

      <Grid
        container
        justifyContent="space-between"
        alignItems={"flex-start"}
        gap={2}
        sx={{ mb: 2 }}
      >
        <Grid item xs={12} lg={7}>
          <Typography>
            How would you like to receive notifications and updates about your
            account?
          </Typography>
        </Grid>

        <Grid item>
          <Select
            sx={{ backgroundColor: "white", width: 270 }}
            value={option}
            onChange={handleChange}
            displayEmpty
          >
            <MenuItem value="">
              <em>Select</em>
            </MenuItem>
            <MenuItem value={OPT_IN_EMAIL}>Email</MenuItem>
            <MenuItem value={OPT_IN_PHONE}>Text Message</MenuItem>
            <MenuItem value={BOTH}>Email and Text Message</MenuItem>
          </Select>
          {(option === BOTH || option === OPT_IN_PHONE) && (
            <Grid sx={{ mt: 2 }}>
              <FormHelperText
                sx={error ? { color: "error.main" } : { color: grey[600] }}
              >
                Input phone number to receive text message{" "}
              </FormHelperText>
              <TextField
                size="small"
                sx={{ backgroundColor: "white", maxWidth: 300 }}
                fullWidth
                name="phoneNumber"
                onChange={handleChangePhone}
                value={normalizePhone(profile?.phoneNumber || "")}
                placeholder="(750) 932-3498"
                error={!!error}
              />
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid
        container
        alignItems={"center"}
        justifyContent="space-between"
        gap={2}
        sx={{ mb: 2 }}
      >
        <Grid item xs={12} lg={7}>
          <Typography>
            Use Bento's Instagram Tagging feature to discover other creators'
            past brand partners! Just share your username and tag @onbento —
            we'll send you a customized pitch list filled with brands they've
            worked with. (
            <Link
              href={
                "https://www.loom.com/share/ec89eca235e34f38b68db2e2de3935b3"
              }
              target="_blank"
              rel="noreferrer"
              sx={{ color: "primary.main", fontWeight: 600 }}
              onClick={() => {
                trackEvent("Instagram Brands Learn More Link Clicked");
              }}
            >
              learn more{" "}
              <Box component="i" className="fa-solid fa-circle-info" />
            </Link>
            )
          </Typography>
        </Grid>

        <TextField
          sx={[styles.textField, { width: 270 }]}
          fullWidth
          placeholder={"username"}
          value={instagramUsername}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment
                sx={styles.linkAdornment}
                disablePointerEvents
                position="start"
              >
                {INSTAGRAM_USERNAME_URL}
              </InputAdornment>
            ),
          }}
          onBlur={handleBlurred}
          onChange={handleChangeInstagramUsername}
          error={!!instagramUsernameValidation}
          helperText={instagramUsernameValidation}
        />
      </Grid>

      <Grid
        container
        alignItems={"center"}
        justifyContent="space-between"
        gap={2}
        sx={{ mb: 2 }}
      >
        <Grid item xs={12} lg={7}>
          <Typography>
            Would you be interested in becoming an affiliate for Bento to earn
            extra income? (
            <Link
              onClick={() => {
                trackEvent("Affiliate Terms and Conditions Clicked");
              }}
              href={AFFILIATE_TERMS_AND_CONDITIONS_LINK}
              target="_blank"
              rel="noreferrer"
              sx={{ color: "primary.main", fontWeight: 600 }}
            >
              Terms & Conditions Apply{" "}
              <Box
                component="i"
                className="fa-regular fa-arrow-up-right-from-square"
                sx={{ ml: 0.5 }}
              />
            </Link>
            )
          </Typography>
        </Grid>
        <Select
          sx={{ backgroundColor: "white", width: 270 }}
          value={joinedAffiliate}
          onChange={(e) => setJoinedAffiliate(e.target.value)}
          displayEmpty
        >
          <MenuItem value={""}>Select</MenuItem>
          <MenuItem value={"true"}>Yes</MenuItem>
          <MenuItem value={"false"}>No</MenuItem>
        </Select>
      </Grid>

      <Grid container sx={{ mt: 6 }}>
        <Grid item xs="auto">
          <Button onClick={handleBack} color="secondary" disableElevation>
            Back
          </Button>
        </Grid>
        <Grid item xs></Grid>
        {handleSkip && (
          <Grid item xs="auto">
            <Button onClick={handleSkip} color="secondary" disableElevation>
              Skip
            </Button>
          </Grid>
        )}
        <Grid item xs="auto">
          <LoadingButton
            onClick={submitForm}
            disableElevation
            variant="contained"
            type="submit"
            loading={loading}
          >
            Next
          </LoadingButton>
        </Grid>
      </Grid>
    </>
  );
};

export default FinalOptInForm;
